/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestHealthFaultAnalyticsInterFace,
  HealthFaultAnalyticsInitialProperties,
  HealthFaultAnalyticsResponseInterface,
} from '../../interface/health-fault-analytics-report-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import dynamicPricing from '../../utils/api/station';

// a thunk to fetch users
export const fetchHealthFaultAnalytics = createAsyncThunk<
  HealthFaultAnalyticsResponseInterface,
  RequestHealthFaultAnalyticsInterFace
>(
  'reports-health-fault-analytics/fetchHealthFaultAnalytics',
  async (data: RequestHealthFaultAnalyticsInterFace, { dispatch }) => {
    try {
      const response = await dynamicPricing.fetchStations(removeEmptyKeys({ ...data }));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListHealthFaultAnalyticsFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'reports-health-fault-analytics/fetchListHealthFaultAnalyticsFilters',
  async (_, { dispatch }) => {
    try {
      const response = await dynamicPricing.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: HealthFaultAnalyticsInitialProperties = {
  healthFaultAnalytics: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const HealthFaultAnalyticsSlice = createSlice({
  name: 'reports-health-fault-analytics',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateHealthFaultAnalyticsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableHealthFaultAnalyticsFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchHealthFaultAnalytics.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHealthFaultAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.healthFaultAnalytics = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchHealthFaultAnalytics.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListHealthFaultAnalyticsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListHealthFaultAnalyticsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListHealthFaultAnalyticsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});
// Export the actions
export const { updateHealthFaultAnalyticsFilters, disableHealthFaultAnalyticsFiltersApplied } =
  HealthFaultAnalyticsSlice.actions;

// Export the reducer
export default HealthFaultAnalyticsSlice.reducer;
